import { useDeferredValue, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { EventSummaryResponse } from '@tabita/contracts';
import { formatDateTime } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { eventsApi } from '@/services/events.api';
import { ApiError } from '@/services/api-client';
import { getShowArchivedRecordsPreference } from '@/services/record-visibility-preferences';
import { Alert, EmptyState, Field, Spinner, StatusBadge } from '@/components/ui';
import { EventForm } from './event-form';

/**
 * The Events directory.
 *
 * Deliberately the same shape as Groups' and People's: same search behaviour,
 * same archived toggle, same empty and error states. A member of staff who
 * has learned one directory has learned all of them.
 */
export function EventsListPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();

  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  // Controlled exclusively by My Settings — no per-page override, so an
  // archived record is never one accidental checkbox away from view.
  const showArchived = getShowArchivedRecordsPreference();

  // Lags one render behind while typing, so a keystroke does not queue a
  // request and the results do not flicker through intermediate states.
  const deferredSearch = useDeferredValue(search);

  const query = useQuery({
    queryKey: ['events', { search: deferredSearch, showArchived }],
    queryFn: ({ signal }) =>
      eventsApi.list(
        { search: deferredSearch || undefined, includeArchived: showArchived, limit: 50 },
        signal,
      ),
    placeholderData: (previous) => previous,
  });

  const events = query.data?.data ?? [];
  const isStale = query.isPlaceholderData;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">{t('events.event.title')}</h1>
          {query.data && (
            <p className="page-count">
              {t('events.event.count', { count: events.length })}
              {query.data.pagination.hasMore && '+'}
            </p>
          )}
        </div>

        {can('events.event.create') && (
          <button
            type="button"
            className="btn"
            data-variant="primary"
            onClick={() => setIsCreating(true)}
          >
            {t('events.event.create')}
          </button>
        )}
      </header>

      <div className="filters">
        <Field
          label={t('common.actions.search')}
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('common.actions.search')}
          name="search"
        />
      </div>

      {query.isError && (
        <Alert tone="danger">
          {query.error instanceof ApiError
            ? t(`errors.${query.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {query.isSuccess && events.length === 0 && (
        <EmptyState
          title={search ? t('common.states.noResults') : t('events.event.empty')}
          hint={search ? undefined : t('events.event.create')}
        />
      )}

      {events.length > 0 && (
        <div className="table-scroll" data-stale={isStale || undefined}>
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('events.event.singular')}</th>
                <th scope="col">{t('events.event.startAt')}</th>
                <th scope="col">{t('events.registration.title')}</th>
                <th scope="col">{t('common.labels.status')}</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <EventRow key={event.id} event={event} locale={locale} />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isCreating && (
        <EventForm
          onClose={() => setIsCreating(false)}
          onCreated={() => {
            setIsCreating(false);
            void query.refetch();
          }}
        />
      )}
    </div>
  );
}

function EventRow({ event, locale }: { event: EventSummaryResponse; locale: string }) {
  const { t } = useI18n();

  return (
    <tr data-archived={event.archivedAt ? true : undefined}>
      <td>
        <Link to={`/events/${event.id}`} className="person-name">
          {event.name}
        </Link>
      </td>
      <td dir="ltr">{formatDateTime(event.startAt, locale)}</td>
      <td>
        {t('events.event.registeredCount', { count: event.registeredCount })}
        {/* The limit is shown beside the count, because "12 registered" means
            something different for an event that seats twelve. */}
        {event.capacity !== null && <span className="cell-empty"> / {event.capacity}</span>}
      </td>
      <td>
        <StatusBadge status={event.status} label={t(`events.status.${event.status}`)} />
      </td>
    </tr>
  );
}
